// React-Query helpers — query key factory + typed wrappers around
// `api()`. Each domain (auth, markets, quote, orders, wallet) gets a
// keys.* namespace so cache invalidation is grep-friendly. Keys are
// arrays per the official React-Query convention; the leading string
// is the resource family.
import { api, qs, APIError } from "./api";
import i18next, { getActiveLocale, DEFAULT_LOCALE } from "./i18n";
import { getActiveMode } from "@/mode/current";
import { accountKey } from "./session";
import { MODE_CASH } from "@/mode/types";
import type {
  TradingCapabilitiesResponse,
  TradingChannelItem,
  TradingChannelListResponse,
} from "@/mode/types";
import type { QueryClient } from "@tanstack/react-query";
import type { SortState } from "@/lib/table";
import type { NotificationItem } from "./notificationCopy";
import type {
  AuthMe,
  PreferencesResponse,
  WatchlistResponse,
  ProfileResponse,
  LanguageItem,
  MFAFactor,
  SessionItem,
  AuthEventItem,
  MarketItem,
  CategoryItem,
  EventItem,
  TickerSnapshot,
  DepthSnapshot,
  TradePrint,
  CandleItem,
  OrderItem,
  PositionItem,
  WalletBalance,
  WalletBalancesResponse,
  WalletCapabilitiesResponse,
  ConversionRoutesResponse,
  ConversionRouteStatus,
  ConversionQuote,
  ConversionItem,
  ConversionExecuteResponse,
  CurrencyInfo,
  CryptoOptions,
  CryptoDepositAddress,
  CryptoDepositRow,
  CryptoWithdrawalRow,
  FeeChargeItem,
  PortfolioActivityItem,
  RedeemPositionResponse,
  RedeemQuote,
  OrderFeePreview,
  ClosePositionResponse,
  PageInfo,
  Paginated,
  CurrencyPresentationResponse,
} from "./types";

// Page-options struct accepted by every list fetcher. Pages call into
// usePagination() / useTableState() which produce the matching
// {limit, offset, [sort]} shape.
export interface PageOpts {
  limit: number;
  offset: number;
  sort?: SortState;
}

// sortQS — collapses a SortState into the `?sort_by=&sort_dir=` pair
// the gateway expects. Returns an empty object when no sort field is
// set so the server falls back to its default.
function sortQS(sort?: SortState) {
  if (!sort?.by) return {};
  return { sort_by: sort.by, sort_dir: sort.dir };
}

// sortKey — stable cache-key fragment that includes both sort field
// and direction (or "default"). Keeping this in one helper means
// every key.*Page() factory gets the same shape without bug-prone
// duplication.
function sortKey(sort?: SortState) {
  if (!sort?.by) return "default" as const;
  return `${sort.by}:${sort.dir}` as const;
}

// marketKey — stable cache-key fragment for a market-scoped feed. Sorted
// + joined so [a,b] and [b,a] hit the same entry; "" when unscoped (the
// standalone portfolio History tab passes no markets).
function marketKey(ids?: string[]) {
  return ids && ids.length ? [...ids].sort().join(",") : "";
}

// localeKey — the active UI locale as a cache-key segment on every catalog
// key family (SM-283/286). Catalog responses are locale-shaped (the server
// overlays translations for `?locale=`), so two languages must never share a
// cache entry; putting the segment in the FACTORY means every consumer keys
// per-locale without touching call sites, and invalidating the parent
// `keys.markets` family still sweeps all locales at once.
const localeKey = () => getActiveLocale();

// modeKey — the active trading mode as a cache-key segment on every
// mode-scoped key family (SM-441). GAME and CASH are two separate books
// with two separate ledgers behind them, so they must never share a
// cache entry; putting the segment in the FACTORY means every consumer
// keys per-mode without touching call sites, and invalidating a parent
// (`keys.trading`, `keys.quote`, `keys.wallet`) still sweeps both modes
// at once. Exactly the localeKey() arrangement, one dimension over.
//
// Which families carry it: trading (orders, portfolio, positions, fees,
// activity), quote (book, depth, candles, prints) and wallet. Which do
// NOT: the catalog. A market's title, rules, outcomes and comments are
// properties of the LOGICAL market and are identical in both modes —
// giving them a mode segment would double every catalog fetch to serve
// identical bytes, and would imply a split that does not exist.
const modeKey = () => getActiveMode();

// accountKey (lib/session.ts) — the session's fixed broker account as a
// cache-key segment on every PRIVATE key family (SM-765): trading
// (orders, positions, fees, activity, redemption quote, fee preview),
// wallet (balances, capabilities, crypto rail, conversions), the bell,
// the fee list and the audit feed. The server scopes each of those
// answers to the session's account (SM-694), so two accounts must never
// share a cache entry; the segment sits right after the family root, so
// invalidating `keys.trading` / `keys.wallet` / `keys.notifications`
// still sweeps every scope and every existing prefix invalidation
// keeps working. Public families (catalog, quote, comments, currencies,
// languages) stay unscoped — they answer the same for everyone.

// modeQS — the `?mode=` fragment mode-scoped fetchers spread into qs().
// Absent when the mode is CASH so existing requests stay byte-identical
// to today's; the gateway treats absent and CASH the same (G3), but not
// sending it keeps the legacy wire unchanged.
function modeQS(): { mode?: string } {
  const mode = getActiveMode();
  return mode === MODE_CASH ? {} : { mode };
}

// modeBody — the same fragment for the one mode-scoped write that
// travels as a JSON BODY rather than a query string (POST /api/orders).
// A query param would not survive: the gateway rebuilds proxy headers
// and forwards the body, and order-service reads `mode` off the body.
function modeBody(): { mode?: string } {
  const mode = getActiveMode();
  return mode === MODE_CASH ? {} : { mode };
}

// localeQS — the `?locale=` fragment catalog fetchers spread into qs().
// Absent (undefined → skipped by qs) when the active language is English so
// existing en requests stay byte-identical to today; the backend treats
// absent and `en` the same, but not sending it keeps the wire unchanged.
function localeQS(): { locale?: string } {
  const active = getActiveLocale();
  return active === DEFAULT_LOCALE ? {} : { locale: active };
}

// CATALOG_PAGE_SIZE is intentionally large because the catalog endpoints
// (rules / topics / categories) feed the joined `fetchMarketGroups`
// aggregator — paging that internal join would mean stitching N round-
// trips per render. The server caps at 200, so we ask for the cap and
// rely on the dataset being O(hundreds) for the foreseeable future.
export const CATALOG_PAGE_SIZE = 200;

// React-Query keys are arrays per the official convention. Paginated
// keys append `limit` + `offset` + `sort` + `filters` so every (page,
// sort, filter) combo gets its own cache entry. Mutations invalidate
// against the *parent* (non-pager) key family — e.g. `keys.orders()`
// for "all order pages" — using React-Query's partial-match
// behaviour, so a paginated detail key doesn't need to be
// reconstructed at invalidation time.
export const keys = {
  auth: ["auth"] as const,
  me: () => [...keys.auth, "me"] as const,
  profile: () => [...keys.auth, "profile"] as const,
  preferences: () => [...keys.auth, "preferences"] as const,
  watchlist: () => [...keys.auth, "watchlist"] as const,
  activePromotion: () => ["promotions", "active"] as const,
  // Enabled UI/catalog languages (public registry, SM-283). Deliberately NOT
  // under keys.markets: the list is locale-independent and must survive the
  // catalog invalidation a language switch performs.
  languages: () => ["languages"] as const,
  mfaFactors: () => [...keys.auth, "mfa-factors"] as const,
  mfaFactorsPage: (
    limit: number,
    offset: number,
    sort?: SortState,
    filters?: { status?: string; kind?: string },
  ) =>
    [
      ...keys.mfaFactors(),
      limit,
      offset,
      sortKey(sort),
      filters?.status ?? "",
      filters?.kind ?? "",
    ] as const,
  sessions: () => [...keys.auth, "sessions"] as const,
  sessionsPage: (
    limit: number,
    offset: number,
    sort?: SortState,
    filters?: { include_revoked?: string },
  ) =>
    [
      ...keys.sessions(),
      limit,
      offset,
      sortKey(sort),
      filters?.include_revoked ?? "",
    ] as const,
  audit: () => [...keys.auth, accountKey(), "audit"] as const,
  /**
   * SM-978: the session broker's GOLD image. Private (auth root, account
   * scope) and keyed by user + broker, so a sign-out or another session
   * drops it instead of painting the previous broker's coin.
   */
  currencyPresentation: (userId: string, brokerId: string) =>
    [...keys.auth, accountKey(), "currency-presentation", userId, brokerId] as const,
  currencyPresentationAll: () => [...keys.auth, accountKey(), "currency-presentation"] as const,
  auditPage: (
    limit: number,
    offset: number,
    sort?: SortState,
    filters?: {
      event_type?: string;
      event_severity?: string;
      date_from?: string;
      date_to?: string;
    },
  ) =>
    [
      ...keys.audit(),
      limit,
      offset,
      sortKey(sort),
      filters?.event_type ?? "",
      filters?.event_severity ?? "",
      filters?.date_from ?? "",
      filters?.date_to ?? "",
    ] as const,

  markets: ["markets"] as const,
  // Every key under the markets/catalog family below carries a localeKey()
  // segment (directly, or inherited by spreading a parent factory) — catalog
  // payloads are locale-shaped, so a language switch must land on fresh
  // entries. `keys.markets` itself stays segment-free: it is the invalidation
  // parent that sweeps every locale.
  marketsList: (status?: string) => [...keys.markets, "list", localeKey(), status ?? "all"] as const,
  marketsListPage: (
    status: string | undefined,
    limit: number,
    offset: number,
    sort?: SortState,
  ) => [...keys.marketsList(status), limit, offset, sortKey(sort)] as const,
  // marketGroups is the joined topic-grouped catalog. The optional
  // `q` (search query) and `category_id` are part of the cache key so
  // each filtered/searched grid keeps its own React-Query entry; the
  // pagination cursor (`limit`/`offset`) is part of the key too because
  // the markets index now drives pages from the backend rather than
  // bulk-loading the catalog and slicing client-side.
  marketGroups: (
    q?: string,
    categoryId?: string,
    limit?: number,
    offset?: number,
  ) =>
    [
      ...keys.markets,
      "groups",
      localeKey(),
      q ?? "",
      categoryId ?? "",
      limit ?? 0,
      offset ?? 0,
    ] as const,
  // Search-results key for the header palette. Separate from
  // marketGroups because the palette uses a smaller page size and a
  // different no-q sentinel (no fetch unless q is set).
  marketSearch: (q: string) => [...keys.markets, "search", localeKey(), q] as const,
  marketDetail: (id: string) => [...keys.markets, "detail", localeKey(), id] as const,
  // Single-market fallback fetch (portfolioLookup fan-out for bulk-join
  // misses). Distinct from marketDetail (which caches a MarketGroup, not a
  // MarketItem) — sharing that key would collide on cache shape. Carries
  // localeKey() and lives under keys.markets so a language switch both
  // caches per-locale and gets swept by the switch-time invalidation.
  catalogMarket: (id: string) => [...keys.markets, "catalog-market", localeKey(), id] as const,
  // Comments — everything for one market group hangs under one prefix so
  // a single invalidate(keys.comments(id)) refreshes feed + replies +
  // summary after a post / vote / delete.
  comments: (groupId: string) => ["comments", groupId] as const,
  // Prefix over every top-level feed (any sort/page size) — invalidate this
  // to refresh root rows without also refetching open reply threads.
  commentsFeeds: (groupId: string) => [...keys.comments(groupId), "feed"] as const,
  commentsFeed: (groupId: string, sort: CommentSort, pageSize: number) =>
    [...keys.commentsFeeds(groupId), sort, pageSize] as const,
  commentReplies: (groupId: string, rootId: string, pageSize: number) =>
    [...keys.comments(groupId), "replies", rootId, pageSize] as const,
  commentSummary: (groupId: string) => [...keys.comments(groupId), "summary"] as const,
  categories: () => [...keys.markets, "categories", localeKey()] as const,
  categoriesPage: (limit: number, offset: number) =>
    [...keys.categories(), limit, offset] as const,
  topics: () => [...keys.markets, "events", localeKey()] as const,
  topicsPage: (limit: number, offset: number) =>
    [...keys.topics(), limit, offset] as const,
  // Single-event fallback fetch, the events-list twin of catalogMarket:
  // the paginated list only carries PUBLISHED events, so a row (or a
  // detail page) whose event has been unpublished resolves it by id
  // instead — SM-322.
  catalogEvent: (id: string) => [...keys.topics(), "catalog-event", id] as const,
  // eventsWithMarkets is the event-paginated home-grid key. Distinct
  // from topicsPage so the admin-style topic listing (which uses the
  // legacy eventResp shape) doesn't collide on cache invalidation —
  // they're different wire shapes despite hitting the same URL.
  eventsWithMarkets: (
    q: string | undefined,
    categoryId: string | undefined,
    status: string | undefined,
    sortBy: string | undefined,
    sortDir: string | undefined,
    marketsLimit: number,
    limit: number,
    offset: number,
  ) =>
    [
      ...keys.topics(),
      "with-markets",
      q ?? "",
      categoryId ?? "",
      status ?? "",
      sortBy ?? "default",
      sortDir ?? "default",
      marketsLimit,
      limit,
      offset,
    ] as const,
  // eventsFeed is the infinite-scroll variant of eventsWithMarkets used
  // by useEventsFeed (Home + Explore). The page cursor (offset) is the
  // useInfiniteQuery pageParam, so it is deliberately NOT in the key —
  // only the filter + page size identify a feed, and every loaded page
  // lives under this single cache entry. `marketsLimit` defaults to the
  // shared FEED_MARKETS_PREVIEW_LIMIT so callers that omit it still key
  // consistently.
  eventsFeed: (filter: EventsWithMarketsFilter, pageSize: number) =>
    [
      ...keys.topics(),
      "feed",
      filter.q ?? "",
      filter.category_id ?? "",
      filter.status ?? "",
      filter.sortBy ?? "default",
      filter.sortDir ?? "default",
      filter.marketsLimit ?? FEED_MARKETS_PREVIEW_LIMIT,
      pageSize,
    ] as const,

  quote: ["quote"] as const,
  ticker: (id: string, frame?: Frame) => [...keys.quote, modeKey(), "ticker", id, frame ?? "A"] as const,
  // Ticker-set queries are keyed by the REQUESTED id set (sorted, so render
  // order doesn't matter). One shared key let a page land on another page's
  // still-fresh array (different markets entirely) — React Query then skipped
  // the mount fetch and the price buttons sat empty until the poll interval
  // fired. Per-id-set keys make every page's first fetch immediate, like depth.
  // `tickersPrefix` is the shared stem: the WS patcher updates every cached id
  // set through it, and the preview routes pin defaults on it.
  tickersPrefix: (frame?: Frame) => [...keys.quote, modeKey(), "tickers", frame ?? "A"] as const,
  tickers: (ids: string[], frame?: Frame) =>
    [...keys.tickersPrefix(frame), [...ids].sort().join(",")] as const,
  depth: (id: string, levels = 20, frame?: Frame) =>
    [...keys.quote, modeKey(), "depth", id, levels, frame ?? "A"] as const,
  candles: (id: string, res: string, limit = 200, frame?: Frame) =>
    [...keys.quote, modeKey(), "candles", id, res, limit, frame ?? "A"] as const,
  trades: (id: string, limit = 50, beforeSeq = 0, frame?: Frame) =>
    [...keys.quote, modeKey(), "trades", id, limit, beforeSeq, frame ?? "A"] as const,

  trading: ["trading"] as const,
  // orderVerdict — client-only slot (written via setQueryData from the order
  // WS stream, never fetched) holding a market's latest engine failure
  // verdict so the trade panel can explain a canceled/rejected order inline.
  // Toasts alone miss users who look away: verdicts can land 30–90 s after
  // submit while the order-status projector drains a market-maker burst.
  // Deliberately NOT under keys.orders() — it must survive the
  // invalidateQueries({ queryKey: keys.orders() }) refresh storm untouched.
  orderVerdict: (marketId: string) => [...keys.trading, accountKey(), "order-verdict", marketId] as const,
  // orderOutcome — sibling client-only slot holding a market's latest order
  // status (written from the same order WS stream). The place POST only returns
  // PENDING_ENGINE, so the trade panel waits on this real engine outcome before
  // showing the success modal — otherwise a self-match / no-liquidity cancel
  // flashes a false "Bought!" before the inline verdict contradicts it. Same as
  // orderVerdict, deliberately NOT under keys.orders() so the invalidation storm
  // leaves it untouched.
  orderOutcome: (marketId: string) => [...keys.trading, accountKey(), "order-outcome", marketId] as const,
  // SM-815: client-only slot (written from the same order WS stream) holding
  // when the external-credit state's follow after a fill or terminal frame
  // ends — read by its poll (useExternalCreditState), never fetched.
  externalCreditFollow: () => [...keys.trading, accountKey(), "external-credit-follow"] as const,
  // SM-666 (15): one order's funding status while its Deduct is pending.
  orderProviderStatus: (orderId: string) => [...keys.trading, accountKey(), "provider-status", orderId] as const,
  orders: () => [...keys.trading, accountKey(), modeKey(), "orders"] as const,
  ordersPage: (
    limit: number,
    offset: number,
    sort?: SortState,
    filters?: { status?: string; side?: string; market_id?: string },
  ) =>
    [
      ...keys.orders(),
      limit,
      offset,
      sortKey(sort),
      filters?.status ?? "",
      filters?.side ?? "",
      filters?.market_id ?? "",
    ] as const,
  // ordersFeed is the infinite-scroll ("Show more") variant of ordersPage,
  // used by the portfolio Open Orders tab. The page cursor (offset) is the
  // useInfiniteQuery pageParam, so it is deliberately NOT in the key — only
  // page size + filters identify a feed, and every loaded page shares one
  // cache entry (mirrors keys.eventsFeed). Prefixed with orders() so a
  // cancelOrder invalidateQueries({ queryKey: orders() }) still refreshes it.
  ordersFeed: (
    pageSize: number,
    filters?: { status?: string; side?: string; market_id?: string; marketIds?: string[]; sort?: SortState },
  ) =>
    [
      ...keys.orders(),
      "feed",
      pageSize,
      filters?.status ?? "",
      filters?.side ?? "",
      filters?.market_id ?? "",
      marketKey(filters?.marketIds),
      sortKey(filters?.sort),
    ] as const,
  portfolio: () => [...keys.trading, accountKey(), modeKey(), "portfolio"] as const,
  portfolioPage: (
    limit: number,
    offset: number,
    sort?: SortState,
    filters?: { status?: string; direction?: string; market_id?: string },
  ) =>
    [
      ...keys.portfolio(),
      limit,
      offset,
      sortKey(sort),
      filters?.status ?? "",
      filters?.direction ?? "",
      filters?.market_id ?? "",
    ] as const,
  // portfolioFeed is the infinite-scroll ("Show more") variant of
  // portfolioPage, used by the market-detail Position panel. Offset is the
  // useInfiniteQuery pageParam, so it is absent from the key; page size +
  // status/direction + market scope identify the feed (mirrors
  // keys.ordersFeed / keys.portfolioActivityFeed). Prefixed with portfolio()
  // so a close/redeem invalidateQueries({ queryKey: portfolio() }) still
  // refreshes it.
  portfolioFeed: (
    pageSize: number,
    filters?: { status?: string; direction?: string; marketIds?: string[]; sort?: SortState },
  ) =>
    [
      ...keys.portfolio(),
      "feed",
      pageSize,
      filters?.status ?? "",
      filters?.direction ?? "",
      marketKey(filters?.marketIds),
      sortKey(filters?.sort),
    ] as const,
  portfolioTrades: () => [...keys.trading, accountKey(), modeKey(), "portfolio-trades"] as const,
  portfolioFees: () => [...keys.trading, accountKey(), modeKey(), "portfolio-fees"] as const,
  portfolioFeesPage: (limit: number, offset: number, marketID?: string) =>
    [...keys.portfolioFees(), limit, offset, marketID ?? ""] as const,
  myFees: () => [...keys.auth, accountKey(), "my-fees"] as const,
  portfolioActivity: () => [...keys.trading, accountKey(), modeKey(), "portfolio-activity"] as const,
  // The redeem quote is per position and deliberately NOT cached across
  // the dialog closing: the rate can change under the user, and a stale
  // quote is what the version check exists to catch.
  redeemQuote: (positionID: string) =>
    [...keys.trading, accountKey(), "redeem-quote", positionID] as const,
  // The trade panel's fee preview (SM-706), keyed on every field the
  // request carries so each distinct order is priced once. Mode-scoped like
  // the rest of trading: the two books resolve fees independently. The
  // no-argument form is the idle key the panel parks on while its inputs
  // are incomplete. `inputsKey` is the caller's name for the inputs the
  // figures will be judged against (useOrderFeePreview): never sent, but
  // part of the key, so a cached answer always carries the name it was
  // asked under.
  orderFeePreview: (input?: OrderFeePreviewInput | null, inputsKey = "") =>
    input
      ? ([
          ...keys.trading,
          accountKey(),
          modeKey(),
          "order-fee-preview",
          input.market_id,
          input.outcome_id ?? "",
          input.side,
          input.price_type,
          input.price,
          input.quantity,
          inputsKey,
        ] as const)
      : ([...keys.trading, accountKey(), modeKey(), "order-fee-preview"] as const),
  portfolioActivityPage: (
    limit: number,
    offset: number,
    sort?: SortState,
    filters?: { bucket?: string; marketIds?: string[] },
  ) =>
    [
      ...keys.portfolioActivity(),
      limit,
      offset,
      sortKey(sort),
      filters?.bucket ?? "",
      marketKey(filters?.marketIds),
    ] as const,
  // portfolioActivityFeed is the infinite-scroll ("Show more") variant of
  // portfolioActivityPage, used by the portfolio History tab and the market-
  // detail activity panel. Offset is the useInfiniteQuery pageParam, so it is
  // absent from the key; page size + bucket + market scope identify the feed
  // (mirrors keys.eventsFeed / keys.ordersFeed). The portfolio tab passes no
  // markets → marketKey "" → the same key shape as before.
  portfolioActivityFeed: (
    pageSize: number,
    filters?: { bucket?: string; marketIds?: string[]; sort?: SortState },
  ) =>
    [
      ...keys.portfolioActivity(),
      "feed",
      pageSize,
      filters?.bucket ?? "",
      marketKey(filters?.marketIds),
      sortKey(filters?.sort),
    ] as const,

  wallet: ["wallet"] as const,
  // The LEGACY single-wallet read. Deliberately mode-free:
  // /api/wallet/balance is GOLD-only for every caller forever, so
  // keying it per mode would refetch identical bytes on every switch.
  // Mode-scoped wallet surfaces use walletBalances() below.
  walletBalance: () => [...keys.wallet, accountKey(), "balance"] as const,
  // The three-wallet read (SM-442). Mode-scoped because what a surface
  // is allowed to show differs: CASH shows GOLD, GAME shows RED+GREEN.
  walletBalances: () => [...keys.wallet, accountKey(), modeKey(), "balances"] as const,
  walletCapabilities: () => [...keys.wallet, accountKey(), "capabilities"] as const,
  // SM-656 / SM-666 (15): an external-credit session's funding state —
  // the provider balance, its fundings and Credits — as the server
  // calculated it. Account-scoped like every private read.
  walletExternalCreditState: () => [...keys.wallet, accountKey(), "external-credit-state"] as const,
  // Crypto rail (SM-212). Everything hangs under one walletCrypto() stem so
  // a single invalidate refreshes options + histories after a withdrawal.
  walletCrypto: () => [...keys.wallet, accountKey(), "crypto"] as const,
  // Keyed on the SCOPE, because a scoped answer describes one currency
  // and serving it to another screen is the collapse SM-514 closed.
  walletCryptoOptions: (currency?: string) =>
    [...keys.walletCrypto(), "options", currency ?? "ALL"] as const,
  // The deposit address is REUSED per user + network + CURRENCY
  // (SM-504) — cache per network AND currency. Keying on the network
  // alone would serve the GOLD address on the RED screen, which is the
  // one mistake this identity exists to make impossible.
  walletCryptoDepositAddress: (networkId: string, currency: string) =>
    [...keys.walletCrypto(), "deposit-address", networkId, currency] as const,
  walletCryptoDeposits: () => [...keys.walletCrypto(), "deposits"] as const,
  walletCryptoDepositsPage: (limit: number, offset: number) =>
    [...keys.walletCryptoDeposits(), limit, offset] as const,
  walletCryptoWithdrawals: () => [...keys.walletCrypto(), "withdrawals"] as const,
  walletCryptoWithdrawalsPage: (limit: number, offset: number) =>
    [...keys.walletCryptoWithdrawals(), limit, offset] as const,

  // The currency registry (SM-442). Public, session-free and shared by
  // every surface that draws money, so it sits at the root rather than
  // under wallet: a signed-out visitor renders prices with it too.
  currencies: () => ["currencies"] as const,
  // Conversion routes + history (SM-417). Not mode-scoped: conversion
  // is the one place the two ledgers legitimately meet (GREEN→GOLD), so
  // scoping it to a mode would hide the user's own history from them.
  conversionRoutes: () => [...keys.wallet, accountKey(), "conversion-routes"] as const,
  conversions: () => [...keys.wallet, accountKey(), "conversions"] as const,
  conversionsPage: (limit: number, offset: number) =>
    [...keys.conversions(), limit, offset] as const,

  // Trading channels of one logical market (SM-441/SM-442). NOT
  // mode-scoped: the response lists BOTH modes, and it is what decides
  // which of them may be offered — keying it per mode would make the
  // answer depend on the question.
  tradingChannels: (logicalMarketID: string) =>
    ["trading-channels", logicalMarketID] as const,

  // Global write-path capability from order-service. This is not a
  // channel key: F5 visibility and F6 initiation are independent axes.
  tradingCapabilities: () => ["trading-capabilities"] as const,

  // The BATCHED channel read (SM-483) — the channels for a whole page
  // of markets in one request, for the grid surfaces.
  //
  // Keyed on the SORTED id set AND the mode, deliberately:
  //   - sorted, so two components asking for the same markets in a
  //     different order share one request instead of racing two;
  //   - per mode, unlike the per-market key above, because this one
  //     asks a mode-scoped question ("the GAME channels of these
  //     markets") rather than returning both modes. Without the mode
  //     in the key a switch would serve the other mode's map from
  //     cache, which is the cross-mode leak in cache form.
  tradingChannelsBulk: (logicalMarketIDs: string[], mode: string) =>
    ["trading-channels", "bulk", mode, [...logicalMarketIDs].sort().join(",")] as const,

  // Server-backed bell feed (notification-service via the gateway).
  // Invalidate the `notifications` root on a WS "notification" frame —
  // it covers both the feed and the unread badge.
  notifications: ["notifications"] as const,
  notificationsFeed: (pageSize: number) =>
    [...keys.notifications, accountKey(), "feed", pageSize] as const,
  notificationsUnread: () => [...keys.notifications, accountKey(), "unread"] as const,
};

// invalidateTradingActivity refreshes every live "your activity" surface after a
// trade-affecting event: the Open Orders feed (keys.orders), the Positions feed
// (keys.portfolio), the History feed (keys.portfolioActivity — the merged
// order/position/fee stream behind /api/portfolio/activity) and every balance
// an order moves. Centralised so a new panel key can't silently drift out of the
// live-refresh set: the detail-page History tab once stopped updating on fills/
// cancels because the order WS stream + place/cancel mutations were invalidating
// the since-removed order-history / portfolio-events key families instead of
// keys.portfolioActivity(). Returns the aggregate promise so callers that
// must wait for the refetch (TradePanel defers its confirmation modal on it)
// can await; fire-and-forget callers (the WS handlers) just ignore it.
export const invalidateTradingActivity = (qc: QueryClient): Promise<unknown> =>
  Promise.all([
    qc.invalidateQueries({ queryKey: keys.orders() }),
    qc.invalidateQueries({ queryKey: keys.portfolio() }),
    qc.invalidateQueries({ queryKey: keys.portfolioActivity() }),
    invalidateOrderBalances(qc),
  ]);

// invalidateOrderBalances refreshes every balance an order moves (SM-815):
// the three reads a band or the header chip draws — the legacy GOLD wallet
// (CASH), the mode's multi-wallet read (GAME) and an external-credit
// session's funding state. They are siblings under [wallet, account], so
// invalidating one never prefix-matches another — each is named. Not the
// whole [wallet, account] stem: it also holds the crypto rail (whose deposit
// address is a get-or-create POST), the conversion routes and history, and
// the capabilities — nothing an order moves, and this runs on every order WS
// frame. A balance-bearing key added later fails
// tradingActivityInvalidation.test.ts until it is classified.
//
// The promise covers the two native reads only. The external-credit state is
// broker-service's composition of the provider snapshot, its fundings and the
// ledger's Credits, and SM-812 makes it a provider GetBalance too — so a
// caller that waits (TradePanel's confirmation, the funding-pending notice, a
// cancel button's spinner) would wait on the provider. It is refetched all
// the same; nothing waits for it.
export const invalidateOrderBalances = (qc: QueryClient): Promise<unknown> => {
  void qc.invalidateQueries({ queryKey: keys.walletExternalCreditState() });
  return Promise.all([
    qc.invalidateQueries({ queryKey: keys.walletBalance() }),
    qc.invalidateQueries({ queryKey: keys.walletBalances() }),
  ]);
};

// Roots whose answers belong to one session: its account's money and
// trading, its bell, its disputes, leagues and promotions, and its
// profile/security reads. The catalog, quotes, comments and currency
// registry are public and survive a session change.
const PRIVATE_ROOTS = new Set(["trading", "wallet", "notifications", "auth", "me", "disputes", "rank", "promotions"]);

// dropPrivateQueries removes every private cache entry when the session
// changes — sign-out, session expiry, or a sign-in as another account in
// the same tab (SM-765). The account segment already keeps two sessions
// from sharing an entry; dropping them as well means nothing a previous
// session read outlives it in memory. The bootstrap itself (keys.me) is
// kept: it is the answer that announced the change.
export const dropPrivateQueries = (qc: QueryClient): void => {
  const me = keys.me();
  qc.removeQueries({
    predicate: (q) => {
      const [root, second] = q.queryKey as readonly unknown[];
      if (root === me[0] && second === me[1]) return false;
      return typeof root === "string" && PRIVATE_ROOTS.has(root);
    },
  });
};

// --- typed fetchers ---

export const fetchMe = () => api<AuthMe>("/api/auth/me");
export const fetchProfile = () => api<ProfileResponse>("/api/me");

// --- Promotional giveaways (promotion-service via the gateway) ---
export interface ActivePromotion {
  campaign_id: string;
  name: string;
  description: string;
  image_url: string;
  button_text: string;
  button_position: "TOP" | "BOTTOM" | "LEFT" | "RIGHT";
  reward_amount_minor: number; // public 4dp minor
}

// The active claim-dialog campaign for the signed-in user, or null. Never
// throws (anonymous / transient errors → null) so it can't block app boot.
export const fetchActivePromotion = async (): Promise<ActivePromotion | null> => {
  try {
    const r = await api<{ promotion: ActivePromotion | null }>("/api/promotions/active");
    return r.promotion ?? null;
  } catch {
    return null;
  }
};

// PROM_000204 — the campaign's funding account can't cover the reward right
// now (SM-152 transfer funding). The claim stays PENDING server-side and
// retries idempotently, so the UI shows friendly copy only — never the
// server message — and the dialog re-appears on the next load.
export const PROMO_FUNDING_INSUFFICIENT = "PROM_000204";

// claimPromotion is the LEGACY claim (pre-SM-615 dialog): source "BUTTON" =
// pressed claim; "CLOSE" = closed the dialog (auto-claim). Idempotent
// server-side and converges with check-in on the same claim row. Only the
// rolling-deployment fallback still calls it.
export const claimPromotion = (campaignId: string, source: "BUTTON" | "CLOSE") =>
  api<{ claimed: boolean; amount_minor: number }>(`/api/promotions/${campaignId}/claim`, {
    method: "POST",
    body: { source },
  });

// --- VISIT check-in (SM-618) ---
//
// A VISIT campaign credits the user for simply being here, signed in, while
// its occurrence is open — there is no claim button. The client checks in on
// authenticated app open, about every minute while open, and whenever the
// tab becomes visible or the connection returns; the server credits the
// current occurrence of every eligible campaign (idempotently — reloads,
// retries and concurrent tabs cannot double-credit) and returns ONLY the
// credits that newly moved money. An empty list is the normal answer.
export type VisitCreditResult = "FULL" | "PARTIAL" | "SKIPPED" | string;

export interface VisitCredit {
  campaign_id: string;
  campaign_name: string;
  /** Wire currency of the credit (RED | GREEN | GOLD) — the record's own unit. */
  currency: string;
  /** ACTUAL credited amount, public 4dp minor (less than the reward on PARTIAL). */
  amount_minor: number;
  result: VisitCreditResult;
  reason?: string;
  run_id: string;
  image_url?: string;
  popup_title?: string;
}

export const checkInPromotions = () =>
  api<{ credits: VisitCredit[] }>("/api/promotions/check-in", { method: "POST", body: {} }).then(
    (r) => ({ credits: r.credits ?? [] }),
  );

// During the rolling deployment an older gateway has no check-in route and
// answers 404/405. That is not an error to show anyone — the legacy
// active-promotion dialog still works against it, so the client falls back.
export function isLegacyPromotionGateway(err: unknown): boolean {
  return err instanceof APIError && (err.status === 404 || err.status === 405);
}

export interface MFAFactorOpts extends PageOpts {
  status?: string;
  kind?: string;
}

export const fetchMFAFactors = (
  opts: MFAFactorOpts,
): Promise<Paginated<MFAFactor>> =>
  api<{ factors: MFAFactor[]; page: PageInfo }>(
    `/api/me/mfa/factors${qs({
      limit: opts.limit,
      offset: opts.offset,
      ...sortQS(opts.sort),
      status: opts.status,
      kind: opts.kind,
    })}`,
  ).then((r) => ({ items: r.factors ?? [], page: r.page }));

export interface SessionOpts extends PageOpts {
  include_revoked?: string;
}

export const fetchSessions = (
  opts: SessionOpts,
): Promise<Paginated<SessionItem>> =>
  api<{ sessions: SessionItem[]; page: PageInfo }>(
    `/api/me/sessions${qs({
      limit: opts.limit,
      offset: opts.offset,
      ...sortQS(opts.sort),
      include_revoked: opts.include_revoked,
    })}`,
  ).then((r) => ({ items: r.sessions ?? [], page: r.page }));

export interface AuditOpts extends PageOpts {
  event_type?: string;
  event_severity?: string;
  date_from?: string;
  date_to?: string;
}

export const fetchAudit = (opts: AuditOpts): Promise<Paginated<AuthEventItem>> =>
  api<{ entries: AuthEventItem[]; page: PageInfo }>(
    `/api/me/audit${qs({
      limit: opts.limit,
      offset: opts.offset,
      ...sortQS(opts.sort),
      event_type: opts.event_type,
      event_severity: opts.event_severity,
      date_from: opts.date_from,
      date_to: opts.date_to,
    })}`,
  ).then((r) => ({ items: r.entries ?? [], page: r.page }));

// Filter options accepted by the rules listing endpoint. `q` is the
// case-insensitive search term (matches rule.name /
// market_id and parent topic.name on the server). `category_id` /
// `event_id` scope the listing to a single category or topic.
export interface MarketsFilter {
  q?: string;
  category_id?: string;
  event_id?: string;
}

export const fetchMarkets = (
  opts: PageOpts,
  status?: string,
  filter?: MarketsFilter,
): Promise<Paginated<MarketItem>> =>
  api<{ markets: MarketItem[]; page: PageInfo }>(
    `/api/catalog/markets${qs({
      status,
      limit: opts.limit,
      offset: opts.offset,
      q: filter?.q,
      category_id: filter?.category_id,
      event_id: filter?.event_id,
      ...sortQS(opts.sort),
      ...localeQS(),
    })}`,
  ).then((r) => ({ items: r.markets ?? [], page: r.page }));

// fetchCatalogMarket is the single-market lookup used by the
// portfolio/history tabs to fill in instrument metadata that the bulk
// `fetchMarketGroups` join missed. The public list endpoint hides
// orchestrator probe rules (`QUOTE-PROBE-*` / `SETTLE-PROBE-*`) and
// caps at 200 rows, so any position on a probe market or on an
// overflowed rule otherwise falls back to a raw market_id label. The
// single-rule endpoint serves probes fine (only DRAFT is rejected),
// so per-id fallbacks resolve them with one round-trip each.
export const fetchCatalogMarket = (marketId: string): Promise<MarketItem> =>
  api<MarketItem>(`/api/catalog/markets/${marketId}${qs({ ...localeQS() })}`);

// fetchMarketByInstrument resolves a single market by its id. Used by
// fetchMarketGroupByHandle to follow an market_id handle to its
// (event_id-grouped) MarketGroup. Goes straight to the single-rule
// endpoint so probe and pagination-overflow rules resolve too.
export const fetchMarketByInstrument = (marketId: string): Promise<MarketItem> =>
  fetchCatalogMarket(marketId);

// MarketGroup is the topic-as-card shape consumed by the index +
// detail pages. A "market" in the Polymarket sense is a topic with
// 1..N outcomes, each outcome being its own rule. Loose rules
// (rule.event_id == "") render as a 1-outcome "topic" using the
// rule's own name as the title.
export interface MarketGroup {
  // id is the URL handle for `/markets/$id` — event_id when grouped,
  // market_id when loose.
  id: string;
  title: string;
  category_id: string;
  category_name?: string;
  // Event-level rules / market_context / description the admin enters in
  // the back-office event form. Threaded from the parent event by
  // groupRules / toMarketGroup so the header "Rules" dialog can surface
  // them in tabs (SM-88 — previously visible only in the admin).
  // Undefined for loose markets (no parent event); those carry their copy
  // on the market itself and surface it in the per-market About tab.
  rules?: string;
  market_context?: string;
  description?: string;
  event_id?: string;
  closes_at?: string;
  markets: MarketItem[];      // 1..N outcomes, sorted by display order
  multi: boolean;         // categorical/scalar kind, or markets.length > 1 (see isMultiKind)
  // icon_url is resolved by groupRules from the topic → first
  // market with an icon → category chain. Cards render <img src>
  // when set and render nothing when the chain is empty — no
  // emoji or slug-letter fallback.
  icon_url?: string;
  // kind drives the EventPage slot registry — picks BinaryHero /
  // CategoricalHero / ScalarThresholdsHero etc. Comes from the parent
  // event's kind (added in migration 0013). Loose rules and pre-0013
  // events default to "binary" so existing pages keep rendering.
  kind: "binary" | "categorical" | "scalar_thresholds";
  // Kind-specific shared config from the parent event — {subject, layout}
  // for categorical, {unit, comparator, numeric_precision} for
  // scalar_thresholds. Threaded so the scalar answer rows can build a
  // LOCALIZED threshold label from structured data (lib/scalar.ts) instead
  // of the English baked into market.name. Undefined for loose markets.
  display_meta?: Record<string, unknown>;
  // markets_total is the absolute outcome count under this event's
  // current filter, set when the source (the new event-paginated home
  // grid) returns a truncated preview in `markets`. Consumers that
  // need the *full* outcome count for display ("13 outcomes",
  // "+11 more outcomes") should read this first and fall back to
  // markets.length when it's undefined — which is the case for legacy
  // callers (fetchMarketGroups bulk + fetchMarketGroupByHandle) that
  // already load every outcome into `markets`.
  markets_total?: number;
  // false when this group has been taken out of the marketplace — the
  // detail page renders read-only (no trade panel, no order actions).
  // `undefined` means "not reported, assume live"; see
  // MarketItem.effective_published. Set by fetchMarketGroupByHandle from
  // the parent event, falling back to the primary market's verdict for
  // loose markets (SM-322).
  effective_published?: boolean;
  // Admin-set leading badge on each outcome row: "crown" (rank medal by
  // chance %), "image" (the choice's market icon), or "none". Threaded
  // from the event; loose rules leave it undefined → crown.
  badge_mode?: "crown" | "image" | "none";
  // How to order this event's child markets: "dynamic" ranks by live
  // chance % (default), "fixed" honors each market's display_order ASC.
  // Drives the card preview + detail answer-list order (see
  // orderOutcomesForDisplay). Loose rules leave it undefined → dynamic.
  outcome_order_mode?: "dynamic" | "fixed";
}

// effectiveOutcomesCount returns the trustworthy outcome count for a
// MarketGroup. Reads markets_total when the source provided it
// (event-paginated home grid → truncated preview), otherwise falls
// back to markets.length (legacy bulk fetchers → full set loaded).
export const effectiveOutcomesCount = (g: MarketGroup): number =>
  g.markets_total ?? g.markets.length;

// isMultiKind reports whether an event's declared kind makes it a
// multi-outcome "answer list" question regardless of how many of its
// child markets are currently published. A categorical / scalar_thresholds
// event is a list even with a SINGLE published option — keying the `multi`
// flag off kind (not just the published-market count) stops a
// half-published categorical event from masquerading as a lone Yes/No
// market until a second option goes live (the admin-reported "event only
// shows the answer once two questions are published" bug). Binary events
// have no kind signal, so they fall back to the >1 count check and a
// genuine one-question event still renders as Yes/No.
export const isMultiKind = (kind?: string): boolean =>
  kind === "categorical" || kind === "scalar_thresholds";

// MarketGroupsPage is the paginated shape consumed by the markets
// index. `groups` is the page of topic-grouped cards built from the
// rules page; `page` is the underlying rules-page metadata so the UI
// can render a "Show more" button keyed off `has_more`. Note: the
// counts are at the rule level, not the topic level — a page of N
// rules can fold into fewer than N visible cards when topics span
// outcomes. The "Show more" CTA stays correct because `has_more`
// reflects whether more rules exist on the server.
export interface MarketGroupsPage {
  groups: MarketGroup[];
  page: PageInfo;
}

// Filter options for the markets index. `q` (search query) and
// `category_id` flow straight to the server; the index calls
// fetchMarketGroupsPage with these to avoid the client-side filter
// pass that the legacy CATALOG_PAGE_SIZE=200 join used to do.
export interface MarketGroupsFilter {
  q?: string;
  category_id?: string;
  status?: string;
}

// fetchMarketGroups bulk-loads the entire (non-paginated) catalog as
// MarketGroup[]. Used by views that need an market_id → group
// lookup table — currently the portfolio page, which renders position
// rows by joining their instrument_ids back to the trader-facing topic
// + rule names. Capped at CATALOG_PAGE_SIZE (200); if the catalog
// outgrows that cap, promote the consumer to a paginated query
// (see fetchMarketGroupsPage) or fold the per-instrument lookup into
// the position payload server-side.
export const fetchMarketGroups = async (): Promise<MarketGroup[]> => {
  const lookupOpts: PageOpts = { limit: CATALOG_PAGE_SIZE, offset: 0 };
  const [marketsPage, topicsPage, catsPage] = await Promise.all([
    fetchMarkets(lookupOpts),
    fetchTopics(lookupOpts),
    fetchCategories(lookupOpts),
  ]);
  return groupRules(marketsPage.items, topicsPage.items, catsPage.items);
};

// fetchMarketGroupsPage drives the markets-index grid. One paginated
// /api/catalog/markets call (filtered by ?q / ?category_id / ?status on
// the server) returns up to `limit` rule rows; we group them by topic
// on the client using one cached topics + categories lookup. Joining
// happens on the client because the wire endpoint is rule-shaped, but
// the filter and pagination are *backend-driven* so a 1000-market
// catalog still pages cleanly without ever shipping 1000 rule rows
// to the browser.
//
// The topics/categories lookup is fetched at CATALOG_PAGE_SIZE — it's
// O(small) (the marketing-facing dataset) and stays warm under React-
// Query's cache, so we trade one cold round-trip for an O(1) join on
// every subsequent page.
export const fetchMarketGroupsPage = async (
  opts: PageOpts,
  filter?: MarketGroupsFilter,
): Promise<MarketGroupsPage> => {
  const lookupOpts: PageOpts = { limit: CATALOG_PAGE_SIZE, offset: 0 };
  const [marketsPage, topicsPage, catsPage] = await Promise.all([
    fetchMarkets(opts, filter?.status, {
      q: filter?.q,
      category_id: filter?.category_id,
    }),
    fetchTopics(lookupOpts),
    fetchCategories(lookupOpts),
  ]);
  return {
    groups: groupRules(marketsPage.items, topicsPage.items, catsPage.items),
    page: marketsPage.page,
  };
};

// EventWithMarkets mirrors market-service's eventWithMarketsResp shape
// (see market-service/internal/adapter/http/dto.go). Returned by
// /api/catalog/events?include=markets — each event row plus a
// truncated preview of its child markets and the absolute post-filter
// count, so the home grid can render `+N more outcomes` without a
// second round-trip.
//
// `markets_total` is the full count under the same filter as the
// preview (publicOnly + status). The preview slice is sorted by
// display_order ASC on the server and capped at the caller's
// markets_limit.
//
// The legacy EventItem fields (event_id, name, kind, icon_url, ...)
// are all here too — the server inlines the eventResp.
export interface EventWithMarkets {
  event_id: string;
  category_id: string;
  name: string;
  description?: string;
  rules?: string;
  market_context?: string;
  display_order: number;
  badge_mode?: "crown" | "image" | "none";
  outcome_order_mode?: "dynamic" | "fixed";
  is_archived: boolean;
  is_published: boolean;
  icon_url?: string;
  kind: "binary" | "categorical" | "scalar_thresholds";
  display_meta: Record<string, unknown>;
  created_by?: string;
  created_at: string;
  updated_at: string;
  markets_total: number;
  markets: MarketItem[];
}

export interface EventsWithMarketsPage {
  events: EventWithMarkets[];
  page: PageInfo;
}

// EventsWithMarketsFilter mirrors the server's query-param surface for
// the event-paginated home grid. `sortBy` is whitelisted server-side
// against the topic sort columns (`name`, `created_at`,
// `display_order`); future `most_interacted` lands here too.
export interface EventsWithMarketsFilter {
  q?: string;
  category_id?: string;
  status?: string;
  sortBy?: string;
  sortDir?: "asc" | "desc";
  marketsLimit?: number;
}

// FEED_MARKETS_PREVIEW_LIMIT is the per-event outcome count Home + Explore
// request (the server caps at 50 — maxMarketsPreviewLimit).
//
// market-service now ranks each event's preview by live Yes ask server-side
// (ListWithMarkets → quote-service), so the cards lead with the true top
// outcomes even at a 2-market preview — the client no longer needs the full
// set to rank. Once that ranking is confirmed live in every environment, drop
// this to 2 (the design's 2-row preview) to stop shipping every outcome just
// to show two.
//
// Kept at 50 for now as a CLIENT-SIDE SAFETY NET: against an older server (or
// one with QUOTE_SERVICE_URL unset → display_order fallback) the card still
// re-ranks the full set via compareOutcomes, so the order is correct
// regardless of deploy ordering. Lowering it makes the cards trust the server.
export const FEED_MARKETS_PREVIEW_LIMIT = 50;

// fetchEventsWithMarkets drives the home-grid event-paginated read.
// One call returns up to `limit` events, each with an inline preview
// of (default 2) markets + the absolute markets_total. The home grid
// renders one card per event and paginates by event count, so
// page.total is finally the same unit as the card count and the
// "Show more (N remaining)" button can render exact math.
export const fetchEventsWithMarkets = (
  opts: PageOpts,
  filter?: EventsWithMarketsFilter,
): Promise<EventsWithMarketsPage> => {
  const marketsLimit = filter?.marketsLimit ?? 2;
  return api<{ events: EventWithMarkets[]; page: PageInfo }>(
    `/api/catalog/events${qs({
      include: "markets",
      markets_limit: marketsLimit,
      limit: opts.limit,
      offset: opts.offset,
      q: filter?.q,
      category_id: filter?.category_id,
      status: filter?.status,
      sort_by: filter?.sortBy,
      sort_dir: filter?.sortDir,
      ...localeQS(),
    })}`,
  ).then((r) => ({ events: r.events ?? [], page: r.page }));
};

// toMarketGroup converts an EventWithMarkets into the legacy
// MarketGroup shape consumed by the card components and the rest of
// the home grid. Keeping the card surface unchanged means MR 4 is a
// data-layer swap, not a UX rewrite.
//
// Inputs:
//   - e: one event row + its preview markets
//   - categoryName: looked up from the cached categories list (the
//     server's eventWithMarketsResp doesn't embed the category name).
//
// Output notes:
//   - `id` follows the legacy URL convention: event_id when grouped,
//     market_id when the event has no markets (defensive fallback —
//     normally an event has ≥1 market under publicOnly filter, or it
//     wouldn't appear at all).
//   - `closes_at` is the MIN of the preview's trading_ends_at /
//     closes_at, matching the per-card "closes in 3d" badge. This is
//     approximate when `markets_total > markets_limit` and the
//     earliest-closing rule lives outside the preview — acceptable
//     because the badge degrades gracefully (relative time).
//   - `multi` is driven by markets_total, not preview length, so a
//     categorical event with 13 outcomes shows as multi even when
//     only 2 are inlined.
//   - icon_url fallback chain: event → first market → category. Empty
//     strings collapse via `||` (matches the backoffice sentinel for
//     "remove icon").
export const toMarketGroup = (
  e: EventWithMarkets,
  categoryName?: string,
  categoryIconURL?: string,
): MarketGroup => {
  const closesCandidates = e.markets
    .map((m) => m.trading_ends_at || m.closes_at)
    .filter((x): x is string => !!x);
  const closesAt =
    closesCandidates.length > 0 ? closesCandidates.slice().sort()[0] : undefined;
  return {
    id: e.event_id || e.markets[0]?.market_id || "",
    title: e.name,
    category_id: e.category_id,
    category_name: categoryName,
    description: e.description,
    rules: e.rules,
    market_context: e.market_context,
    event_id: e.event_id || undefined,
    closes_at: closesAt,
    markets: e.markets,
    // markets_total carries the server-side absolute count under the
    // current filter, which can exceed markets.length when the preview
    // is truncated. effectiveOutcomesCount() reads it for accurate
    // "N outcomes" / "+N more outcomes" rendering on the home grid.
    markets_total: e.markets_total,
    multi: isMultiKind(e.kind) || e.markets_total > 1,
    icon_url:
      e.icon_url ||
      e.markets.find((m) => m.icon_url)?.icon_url ||
      categoryIconURL,
    kind: e.kind,
    display_meta: e.display_meta,
    badge_mode: e.badge_mode,
    outcome_order_mode: e.outcome_order_mode,
  };
};

// groupRules folds a flat rules array into the topic-grouped MarketGroup
// shape. Pure function — same join the legacy fetchMarketGroups used to
// run, factored out so both the paginated page-fetcher and the
// search-results fetcher share one implementation.
function groupRules(
  markets: MarketItem[],
  topics: EventItem[],
  cats: CategoryItem[],
): MarketGroup[] {
  const topicById: Record<string, EventItem> = Object.fromEntries(
    topics.map((t) => [t.event_id, t]),
  );
  const catById: Record<string, CategoryItem> = Object.fromEntries(
    cats.map((c) => [c.category_id, c]),
  );

  const byTopic = new Map<string, MarketItem[]>();
  const loose: MarketItem[] = [];
  for (const r of markets) {
    if (r.event_id) {
      const arr = byTopic.get(r.event_id) ?? [];
      arr.push(r);
      byTopic.set(r.event_id, arr);
    } else {
      loose.push(r);
    }
  }

  const groups: MarketGroup[] = [];
  // Preserve server-side ordering: the first rule we saw for each topic
  // anchors the group's position, so the page reads top-to-bottom in
  // the same created_at DESC order the backend returned.
  const topicOrder: string[] = [];
  for (const r of markets) {
    if (r.event_id && !topicOrder.includes(r.event_id)) topicOrder.push(r.event_id);
  }
  for (const eventId of topicOrder) {
    const topicMarkets = byTopic.get(eventId) ?? [];
    const topic = topicById[eventId];
    const catId = topic?.category_id ?? topicMarkets[0].category_id;
    const cat = catById[catId];
    const earliestClose = topicMarkets
      .map((r) => r.closes_at)
      .filter((x): x is string => !!x)
      .sort()[0];
    groups.push({
      id: eventId,
      title: topic?.name ?? i18next.t("markets:untitledEvent"),
      category_id: catId,
      category_name: cat?.name,
      description: topic?.description,
      rules: topic?.rules,
      market_context: topic?.market_context,
      event_id: eventId,
      closes_at: earliestClose,
      markets: topicMarkets.slice().sort((a, b) =>
        a.market_id.localeCompare(b.market_id),
      ),
      multi: isMultiKind(topic?.kind) || topicMarkets.length > 1,
      // Icon fallback chain: topic → first market under the topic →
      // category. We prefer the topic icon because a multi-rule event
      // wants a single shared logo; if the admin only set a per-market
      // icon (common with Polymarket-style imports where the icon
      // lives on the market row), fall back to that before the
      // category icon so the card isn't iconless. Empty string is
      // treated as missing by `||` — matches the backoffice "remove
      // icon" sentinel which writes "" to the column.
      icon_url:
        topic?.icon_url ||
        topicMarkets.find((m) => m.icon_url)?.icon_url ||
        cat?.icon_url,
      kind: topic?.kind ?? "binary",
      display_meta: topic?.display_meta,
      outcome_order_mode: topic?.outcome_order_mode,
      badge_mode: topic?.badge_mode,
    });
  }
  for (const r of loose) {
    groups.push({
      id: r.market_id,
      title: r.name || r.market_id,
      category_id: r.category_id,
      category_name: catById[r.category_id]?.name,
      closes_at: r.closes_at,
      markets: [r],
      multi: false,
      // Loose rules don't have a topic; prefer the per-market icon
      // (added in migration 0014 — admins can upload an icon
      // directly on the market row) and fall back to the parent
      // category icon for backfilled rules.
      icon_url: r.icon_url || catById[r.category_id]?.icon_url,
      // Loose rules predate the event layer; binary is the only kind
      // that makes sense for them.
      kind: "binary",
    });
  }
  return groups;
}

// fetchMarketSearch is the header palette's debounced backend search.
// Smaller page (20 results) and always q-driven — no `q` means no
// fetch (the calling component should gate on q.length > 0). Returns
// the same MarketGroup[] shape the palette already renders so only
// the data plumbing changes, not the row template.
export const fetchMarketSearch = async (q: string): Promise<MarketGroup[]> => {
  const trimmed = q.trim();
  if (trimmed === "") return [];
  const lookupOpts: PageOpts = { limit: CATALOG_PAGE_SIZE, offset: 0 };
  const [marketsPage, topicsPage, catsPage] = await Promise.all([
    fetchMarkets({ limit: 20, offset: 0 }, undefined, { q: trimmed }),
    fetchTopics(lookupOpts),
    fetchCategories(lookupOpts),
  ]);
  return groupRules(marketsPage.items, topicsPage.items, catsPage.items);
};

// Resolve a `/markets/$id` param to its MarketGroup. The param can
// be either a event_id (grouped) or an market_id (loose, or a
// specific outcome under a multi-rule topic). On market_id hits
// where the rule has a topic, we promote up to the topic group so
// the detail page always renders the full outcome list.
//
// This used to consume the bulk-loaded fetchMarketGroups; now it goes
// straight to the rule-level endpoints because the catalog is no
// longer guaranteed to be in cache as one big page.
export const fetchMarketGroupByHandle = async (
  handle: string,
): Promise<MarketGroup> => {
  const lookupOpts: PageOpts = { limit: CATALOG_PAGE_SIZE, offset: 0 };
  // Try as event_id first: list every rule under that topic. This read is
  // event-scoped, so the server serves unpublished children too and each
  // row carries its own effective_published (SM-322).
  const byTopic = await fetchMarkets(lookupOpts, undefined, {
    event_id: handle,
  }).catch(() => ({ items: [], page: { total: 0, limit: 0, offset: 0, has_more: false } } as Paginated<MarketItem>));
  if (byTopic.items.length > 0) {
    const [topicsPage, catsPage] = await Promise.all([
      fetchTopics(lookupOpts),
      fetchCategories(lookupOpts),
    ]);
    // The events LIST carries published events only. When the handle
    // isn't in it, resolve the event by id so an unpublished (or
    // paginated-past) event still contributes its title, kind and
    // display_meta — without it the page falls back to the market's own
    // question and renders as a lone Yes/No.
    let events = topicsPage.items;
    let event = events.find((e) => e.event_id === handle);
    if (!event) {
      event = await fetchCatalogEvent(handle).catch(() => undefined);
      if (event) events = [...events, event];
    }
    const groups = groupRules(byTopic.items, events, catsPage.items);
    if (groups.length > 0) {
      // Prefer the EVENT's verdict — it's the one that answers "is this
      // whole page tradable"; a per-market flag only speaks for its own
      // outcome. Loose/unknown falls through to undefined = live.
      return { ...groups[0], effective_published: event?.effective_published };
    }
  }
  // Otherwise treat the handle as an market_id — loose rule, or
  // a specific outcome under a topic. If the rule has a event_id we
  // promote up to the topic group so the detail page renders the full
  // outcome list (matching the legacy resolver's behaviour).
  try {
    const rule = await fetchMarketByInstrument(handle);
    if (rule.event_id) {
      return fetchMarketGroupByHandle(rule.event_id);
    }
    const [catsPage] = await Promise.all([fetchCategories(lookupOpts)]);
    const cat = catsPage.items.find((c) => c.category_id === rule.category_id);
    return {
      id: rule.market_id,
      title: rule.name || rule.market_id,
      category_id: rule.category_id,
      category_name: cat?.name,
      closes_at: rule.closes_at,
      markets: [rule],
      multi: false,
      kind: "binary",
      // A loose market has no parent event, so its own verdict IS the
      // page's.
      effective_published: rule.effective_published,
    };
  } catch {
    throw new APIError(
      404,
      "MARKET_NOT_FOUND",
      `No market group for handle "${handle}"`,
      null,
    );
  }
};
export const fetchCategories = (
  opts: PageOpts,
): Promise<Paginated<CategoryItem>> =>
  api<{ categories: CategoryItem[]; page: PageInfo }>(
    `/api/catalog/categories${qs({ limit: opts.limit, offset: opts.offset, ...localeQS() })}`,
  ).then((r) => ({ items: r.categories ?? [], page: r.page }));

// Optional filter for fetchTopics. The events endpoint takes `?q=` and
// `?category_id=` exactly like the rules endpoint (see
// market-service/internal/adapter/http/topics_handlers.go) — both flow
// straight to the SQL filter. `status` is intentionally absent because
// events themselves don't carry one; status lives on each rule.
export interface TopicsFilter {
  q?: string;
  category_id?: string;
}

export const fetchTopics = (
  opts: PageOpts,
  filter?: TopicsFilter,
): Promise<Paginated<EventItem>> =>
  api<{ events: EventItem[]; page: PageInfo }>(
    `/api/catalog/events${qs({
      limit: opts.limit,
      offset: opts.offset,
      q: filter?.q,
      category_id: filter?.category_id,
      ...localeQS(),
    })}`,
  ).then((r) => ({ items: r.events ?? [], page: r.page }));

// fetchCatalogEvent is the per-id event read (SM-322) — the events-list
// twin of fetchCatalogMarket. /api/catalog/events only lists PUBLISHED
// events, so anything that needs an event's title / kind / display_meta
// after an unpublish (a portfolio or history row's parent title, the
// read-only detail page's answer labels) has to resolve it by id.
export const fetchCatalogEvent = (eventId: string): Promise<EventItem> =>
  api<EventItem>(`/api/catalog/events/${eventId}${qs({ ...localeQS() })}`);

// fetchLanguages — the enabled UI/catalog language registry (SM-283).
// Public + anonymous; the selector surfaces render native_name in
// display_order. NO ?locale here — the row set is locale-independent
// (each row carries its own native_name).
export const fetchLanguages = (): Promise<LanguageItem[]> =>
  api<{ languages: LanguageItem[] }>("/api/languages").then((r) => r.languages ?? []);

// Frame mirrors `contracts/httpapi/v1.Frame` — "A" is canonical
// (primary-outcome view, what the engine matches in), "B" is the
// inverted view served pre-flipped by quote-service. Optional on
// every fetcher; the gateway forwards `?frame=` straight through and
// omitting it defaults to "A" so legacy callers don't change shape.
export type Frame = "A" | "B";

export const fetchTicker = (id: string, frame?: Frame) =>
  api<TickerSnapshot>(`/api/quote/markets/${id}/ticker${qs({ frame })}`);
// TICKERS_CHUNK bounds how many market_ids ride on a single /tickers GET.
// Home/Explore now rank a whole page of events by live price, so the joined
// id list can run to hundreds of ULIDs — long enough to blow past an ingress
// URI-length cap (→ 414). Chunking keeps each query string small; the chunks
// fetch in parallel and merge, so callers still get one flat snapshot array.
const TICKERS_CHUNK = 50;
export const fetchTickers = (instruments?: string[], frame?: Frame): Promise<TickerSnapshot[]> => {
  const ids = instruments ?? [];
  if (ids.length > TICKERS_CHUNK) {
    const chunks: string[][] = [];
    for (let i = 0; i < ids.length; i += TICKERS_CHUNK) chunks.push(ids.slice(i, i + TICKERS_CHUNK));
    return Promise.all(chunks.map((c) => fetchTickers(c, frame))).then((parts) => parts.flat());
  }
  // Base case (also the empty/undefined → "all tickers" call): one request.
  // The quote endpoint filters on `instrument_ids`; sending the wrong param
  // name made it ignore the filter and fall back to "100 most-recently-updated
  // tickers" (ListAll), so any market outside that global top-100 — common once
  // the catalog grew past 100 markets — rendered with no price on its button.
  return api<{ tickers: TickerSnapshot[] }>(
    `/api/quote/tickers${qs({ instrument_ids: instruments?.join(","), frame })}`,
  ).then((r) => r.tickers ?? []);
};
export const fetchDepth = (id: string, levels = 20, frame?: Frame) =>
  api<DepthSnapshot>(`/api/quote/markets/${id}/depth${qs({ levels, frame })}`);
export const fetchCandles = (
  id: string,
  resolution: string,
  limit = 200,
  frame?: Frame,
) =>
  api<{ candles: CandleItem[] }>(
    `/api/quote/markets/${id}/candles${qs({ resolution, limit, frame })}`,
  ).then((r) => r.candles ?? []);
// fetchTrades is the cursor-paginated trade tape reader. The first
// page omits beforeSeq; subsequent pages pass the previous response's
// next_cursor as beforeSeq. The full envelope (trades + pagination
// metadata) is returned so the UI can drive prev/next without a
// second roundtrip to learn whether more rows exist.
export interface TradesPage {
  trades: TradePrint[];
  next_cursor: number;
  has_more: boolean;
}
export const fetchTrades = (
  id: string,
  limit = 50,
  beforeSeq = 0,
  frame?: Frame,
): Promise<TradesPage> =>
  api<TradesPage>(
    `/api/quote/markets/${id}/trades${qs({
      limit,
      frame,
      ...(beforeSeq > 0 ? { before_sequence: beforeSeq } : {}),
    })}`,
  ).then((r) => ({
    trades: r.trades ?? [],
    next_cursor: r.next_cursor ?? 0,
    has_more: !!r.has_more,
  }));

export interface OrderOpts extends PageOpts {
  status?: string;
  side?: string;
  market_id?: string;
  /** Scope to a set of markets (an event's member markets), sent as
   *  repeated ?market_id= params. order-service filters server-side via
   *  `market_id = ANY(...)`. Takes precedence over the single market_id. */
  marketIds?: string[];
}

export const fetchOrders = (opts: OrderOpts): Promise<Paginated<OrderItem>> =>
  api<{ orders: OrderItem[]; page: PageInfo }>(
    `/api/orders${qs({
      limit: opts.limit,
      offset: opts.offset,
      ...sortQS(opts.sort),
      status: opts.status,
      side: opts.side,
      market_id: opts.marketIds?.length ? opts.marketIds : opts.market_id,
      ...modeQS(),
    })}`,
  ).then((r) => ({ items: r.orders ?? [], page: r.page }));

export interface PortfolioOpts extends PageOpts {
  status?: string;
  direction?: string;
  market_id?: string;
  /** Scope to a set of markets (an event's member markets), sent as
   *  repeated ?market_id= params. settlement-service filters server-side
   *  via `market_id = ANY(...)`. Takes precedence over the single market_id. */
  marketIds?: string[];
}

export const fetchPortfolio = (
  opts: PortfolioOpts,
): Promise<Paginated<PositionItem>> =>
  api<{ positions: PositionItem[]; page: PageInfo }>(
    `/api/portfolio${qs({
      limit: opts.limit,
      offset: opts.offset,
      ...sortQS(opts.sort),
      status: opts.status,
      direction: opts.direction,
      market_id: opts.marketIds?.length ? opts.marketIds : opts.market_id,
      ...modeQS(),
    })}`,
  ).then((r) => ({ items: r.positions ?? [], page: r.page }));

export const fetchWalletBalance = () => api<WalletBalance>("/api/wallet/balance");

// --- SM-442: the currency registry ---
//
// Presentation only — display name, symbol, image, colour, decimals —
// plus the operator switches. It is NOT the permission answer: what a
// user may actually do comes from fetchWalletCapabilities below, which
// folds the flags and provider readiness the registry cannot see.
//
// Degrades to [] rather than throwing when F1 is dark: the SM-443
// contract has a frozen seeded fallback for every field, so an
// unconfigured app renders identically everywhere instead of blank.
export const fetchCurrencies = (): Promise<CurrencyInfo[]> =>
  api<{ currencies: CurrencyInfo[] }>("/api/currencies")
    .then((r) => r.currencies ?? [])
    .catch((err) => {
      if (err instanceof APIError && (err.status === 404 || err.status === 403)) return [];
      throw err;
    });

// --- SM-978: the session broker's GOLD image ---
//
// `{ GOLD: { image_url, source_broker_id } }`, or `{}` for the platform's
// Gold. The gateway takes the broker from the session; nothing here names
// one. Cosmetic: any failure — 401, 404, a network error, a bad body — is
// the platform image, never an error on screen.
export const fetchCurrencyPresentation = (): Promise<CurrencyPresentationResponse> =>
  api<CurrencyPresentationResponse>("/api/me/currency-presentation")
    .then((r) => (r && typeof r === "object" && !Array.isArray(r) ? r : {}))
    .catch(() => ({}));

// --- SM-442/SM-415: the three-wallet read ---
//
// RED + GREEN + GOLD in one answer. Never summed (G8) — the three are
// different ledgers that happen to share a numeric scale.
export const fetchWalletBalances = () =>
  api<WalletBalancesResponse>("/api/wallet/balances");

// --- SM-419: effective deposit/withdraw capabilities ---
//
// The ledger's whole decision per currency per direction, with a stable
// reason code for every false. Advisory for rendering only: the
// permission layer re-checks at initiation, so a stale read here can
// never authorize a prohibited rail — it can only mislabel a button.
export const fetchWalletCapabilities = () =>
  api<WalletCapabilitiesResponse>("/api/wallet/capabilities");

// --- SM-417: GREEN conversion ---

export const fetchConversionRoutes = (): Promise<ConversionRouteStatus[]> =>
  api<ConversionRoutesResponse>("/api/wallet/conversions/routes").then((r) => r.routes ?? []);

/**
 * Price a conversion. The quote reserves NOTHING — a thousand open
 * quotes move no balance and no pool — so it is safe to re-quote freely
 * when one expires, and an expired quote is re-priced rather than
 * "extended".
 */
export const quoteConversion = (input: {
  source_currency: string;
  destination_currency: string;
  amount: string;
}) =>
  api<ConversionQuote>("/api/wallet/conversions/quote", { method: "POST", body: input });

/**
 * Execute a quoted conversion.
 *
 * `operation_id` is the idempotency key and it rides in the BODY, never
 * a header: the gateway rebuilds proxy headers from scratch and has
 * eaten a header-borne key before (SM-347). The caller must generate it
 * ONCE per user intent and reuse it across every retry — that is what
 * makes a double-click, a flaky network and a mid-flight refresh all
 * resolve to exactly one conversion.
 *
 * `rate_version_id` must be the quote's. If the operator repriced the
 * route in between, the server rejects with LEDGER_000803 and zero
 * state changes, and the user re-quotes at the new rate.
 */
export const executeConversion = (input: {
  quote_id: string;
  rate_version_id: string;
  operation_id: string;
}) =>
  api<ConversionExecuteResponse>("/api/wallet/conversions", { method: "POST", body: input });

export const fetchConversions = (
  opts: PageOpts,
): Promise<Paginated<ConversionItem>> =>
  api<{ conversions: ConversionItem[]; page: PageInfo }>(
    `/api/wallet/conversions${qs({ limit: opts.limit, offset: opts.offset })}`,
  ).then((r) => ({ items: r.conversions ?? [], page: r.page }));

// --- SM-441/SM-442: trading channels ---
//
// GET /api/markets/{id}/channels — the channels of one LOGICAL market,
// both modes in one answer, each with the folded effective_enabled
// verdict. This is the only source for a GAME channel id: the mapping
// is one-way and lives in market-service, so a client can neither
// derive it nor recover the market id from it.
//
// Returns [] rather than throwing when the endpoint is dark (F5 off, or
// a gateway that predates SM-442): "no channels reported" collapses to
// "GAME is not offered here", which is the correct read and keeps CASH
// working untouched.
export const fetchTradingChannels = (
  logicalMarketID: string,
): Promise<TradingChannelItem[]> =>
  api<TradingChannelListResponse>(`/api/markets/${logicalMarketID}/channels`)
    .then((r) => r.channels ?? [])
    .catch((err) => {
      if (err instanceof APIError && err.status === 404) return [];
      throw err;
    });

/** Effective order-initiation gates, reported by order-service itself. */
export const fetchTradingCapabilities = (): Promise<TradingCapabilitiesResponse> =>
  api<TradingCapabilitiesResponse>("/api/trading/capabilities");

/**
 * GET /api/markets/channels?mode=…&market_id=… — the channels of MANY
 * logical markets in one request (SM-483).
 *
 * The grids (home feed, watchlist, portfolio marks, market activity)
 * read a whole page of markets at once. Resolving them one at a time
 * through fetchTradingChannels would be N requests before the first
 * price could render, and pulling the entire GAME set instead is
 * silently wrong past one page: a market that falls off the end
 * resolves to nothing.
 *
 * Chunked at CHANNEL_BULK_CHUNK so a long watchlist cannot exceed the
 * server's id cap. The chunks are fetched in parallel and merged; a
 * single failed chunk fails the whole call rather than yielding a
 * partial map, because a partial map is exactly what callers must not
 * be handed (see useTradingChannelIds).
 *
 * Returns [] rather than throwing when the endpoint is dark (F5 off, or
 * a gateway that predates SM-483), matching fetchTradingChannels.
 */
export const CHANNEL_BULK_CHUNK = 100;

export const fetchTradingChannelsBulk = async (
  logicalMarketIDs: string[],
  mode: string,
): Promise<TradingChannelItem[]> => {
  const ids = [...new Set(logicalMarketIDs.filter(Boolean))];
  if (ids.length === 0) return [];
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += CHANNEL_BULK_CHUNK) {
    chunks.push(ids.slice(i, i + CHANNEL_BULK_CHUNK));
  }
  const results = await Promise.all(
    chunks.map((chunk) =>
      api<TradingChannelListResponse>(
        `/api/markets/channels${qs({ mode, market_id: chunk.join(",") })}`,
      )
        .then((r) => r.channels ?? [])
        .catch((err) => {
          if (err instanceof APIError && err.status === 404) return [];
          throw err;
        }),
    ),
  );
  return results.flat();
};

// --- Crypto wallet rail (SM-212, custodial v1) ---
// All money semantics live on the types in lib/types.ts; the fetchers are
// deliberately thin. user_id is gateway-injected from the session cookie —
// never sent by the client.

// The catalog, optionally scoped to ONE platform currency (SM-514).
//
// Scoping is not a filter for convenience. Unscoped, the payload's
// legacy single-rate fields (`rate`, `credited_currency`) describe
// whichever deposit route happens to come first, so a Red screen
// reading them quotes Gold's rate and Gold's availability. Naming the
// currency makes that collapse impossible on the wire rather than
// something every caller has to remember to avoid.
export const fetchCryptoOptions = (currency?: string) =>
  api<CryptoOptions>(
    currency
      ? `/api/wallet/crypto/options?currency=${encodeURIComponent(currency)}`
      : "/api/wallet/crypto/options",
  );

// POST, but semantically get-or-create: the server reuses one address
// per user + network + currency, so this is safe to drive from useQuery
// with a long staleTime.
//
// The currency is not decoration. It is part of the destination's
// identity (SM-504): asking for RED returns a DIFFERENT address than
// asking for GOLD, and the deposit pipeline credits whichever currency
// the address it arrived at belongs to.
export const createCryptoDepositAddress = (networkId: string, currency: string) =>
  api<CryptoDepositAddress>("/api/wallet/crypto/deposit-address", {
    method: "POST",
    body: { network_id: networkId, currency },
  });

export const fetchCryptoDeposits = (
  opts: PageOpts,
): Promise<Paginated<CryptoDepositRow>> =>
  api<{ deposits: CryptoDepositRow[]; page: PageInfo }>(
    `/api/wallet/crypto/deposits${qs({ limit: opts.limit, offset: opts.offset })}`,
  ).then((r) => ({ items: r.deposits ?? [], page: r.page }));

export const fetchCryptoWithdrawals = (
  opts: PageOpts,
): Promise<Paginated<CryptoWithdrawalRow>> =>
  api<{ withdrawals: CryptoWithdrawalRow[]; page: PageInfo }>(
    `/api/wallet/crypto/withdrawals${qs({ limit: opts.limit, offset: opts.offset })}`,
  ).then((r) => ({ items: r.withdrawals ?? [], page: r.page }));

export interface CreateCryptoWithdrawalInput {
  asset_id: string;
  destination: string;
  /** Base-unit decimal string at the asset's token_decimals. */
  amount_base: string;
}

// 201 → the created withdrawal row. Errors arrive as the standard
// { error: { code, message } } envelope with CWALLET_* codes whose messages
// are user-facing — surface them verbatim (see humanizeCryptoError).
export const createCryptoWithdrawal = (input: CreateCryptoWithdrawalInput) =>
  api<CryptoWithdrawalRow>("/api/wallet/crypto/withdrawals", {
    method: "POST",
    body: input,
  });

// --- mutations (raw — components use TanStack `useMutation`) ---

// Mirrors contracts/httpapi/v1.ClientPlaceOrderRequest exactly.
// Wire field names matter — `tif`/`command_id` will get rejected by
// order-service with "ORDER_000102: invalid price_type" because the
// decoder defaults PriceType to "" when the field is missing under
// the wrong name. So we keep the keys as the contract spells them.
export interface PlaceOrderInput {
  /** The placement idempotency key, and the only one — order-service
   *  dedups on (user_id, client_order_id). Optional, but omitting it
   *  means NO dedup: a keyless placement is always a new order.
   *
   *  A sibling `idempotency_key` used to ride along here. Order-service
   *  never read it, so it was dropped from contracts on 2026-09-02;
   *  don't reintroduce it expecting retry safety.
   *
   *  Minting a fresh value per attempt (what both call sites do today)
   *  buys nothing either: to make a retry idempotent, REUSE the value
   *  across retries of the same user intent. */
  client_order_id?: string;
  market_id: string;
  /** Outcome the user is trading on the rule (e.g. the YES or NO
   *  outcome's outcome_id, or a custom code like "TRUMP"). Optional
   *  during phase 0; from phase 2 the web client always sends it and
   *  order-service translates (side, price) into book space per
   *  documents/order-creation-flow.md §"Outcome encoding". */
  outcome_id?: string;
  side: "BUY" | "SELL";
  price_type: "LIMIT" | "MARKET";
  // Matching-engine TIFs (per order-creation-flow.md, ValidateCombo):
  //   - MARKET allows FOK or FAK (Fill-and-Kill, == IOC).
  //   - LIMIT  allows GTC, GTD, FOK, FAK.
  // GTD additionally requires `expire_at_ms` to be set.
  time_in_force: "GTC" | "GTD" | "FOK" | "FAK";
  /** int64 minor units as a decimal string. For LIMIT this is the
   *  resting price; for MARKET this is the worst-price slippage limit
   *  (ceiling for BUY, floor for SELL) — the engine refuses fills
   *  outside it. Both must be > 0 and within the rule's price band. */
  price: string;
  quantity: string;
  expire_at_ms?: number;
  /**
   * Which book to place on. Stamped by placeOrder from the active mode
   * — callers do not set it, and an override here wins only because
   * spreading the caller's input last is what makes a test able to pin
   * one explicitly.
   */
  mode?: string;
}

/**
 * Place an order on the ACTIVE mode's book (SM-532).
 *
 * `mode` is stamped here rather than by each caller. The wire defaults
 * an absent mode to CASH — gateway and order-service both — so a GAME
 * order that forgot the field is not rejected, it is quietly placed on
 * the CASH book against the trader's cash balance. That is not a
 * failure any caller could notice, which is exactly why the field
 * cannot be a caller's responsibility.
 *
 * Omitted for CASH, matching modeQS(): absent and CASH mean the same
 * thing upstream, and not sending it keeps the legacy request bytes
 * unchanged (the SM-407 golden fixtures pin them).
 */
export const placeOrder = (input: PlaceOrderInput) =>
  api<{ order_id: string; command_id: string; status: string }>(
    "/api/orders",
    { method: "POST", body: { ...modeBody(), ...input } },
  );

/**
 * The placement fields an order's fees depend on (contracts
 * OrderFeePreviewRequest). Deliberately a Pick of PlaceOrderInput: the
 * preview must price the order that WOULD be placed, so the trade panel
 * builds both bodies from one derivation rather than two that can drift.
 */
export type OrderFeePreviewInput = Pick<
  PlaceOrderInput,
  "market_id" | "outcome_id" | "side" | "price_type" | "price" | "quantity" | "mode"
>;

/**
 * Price an order before it is placed (SM-706): exchange fee, broker
 * commission, their worst-case total, and what a BUY must fund / a SELL
 * nets. The server runs the same resolution and rounding code as order
 * acceptance, which is the point — the client never prices a fee from a
 * rate. Amounts come back CANONICAL 8dp (see OrderFeePreview).
 *
 * A POST because it mirrors the placement body, but it moves nothing.
 * `mode` is stamped exactly as placeOrder stamps it, so the preview and the
 * order it previews always address the same book. Identity and the broker
 * account come from the session, never from this body.
 */
export const fetchOrderFeePreview = (input: OrderFeePreviewInput, signal?: AbortSignal) =>
  api<OrderFeePreview>("/api/orders/fee-preview", {
    method: "POST",
    body: { ...modeBody(), ...input },
    signal,
  });

export const cancelOrder = (orderId: string) =>
  api<{ ok: boolean }>(`/api/orders/${orderId}/cancel`, { method: "POST" });

// fetchPortfolioActivity drives the History tab. Single backend-paged
// stream that merges order_history + position_events + fee_charges
// behind one endpoint; the gateway does the bucket filter, sort, and
// pagination so the client only has to render. The previous client-
// side merge fetched 3 endpoints in parallel and recombined them in
// memory; this collapses to one query with a stable sort key.
export interface PortfolioActivityOpts extends PageOpts {
  /** "" / "all" → no filter. Anything else is forwarded as ?bucket=.
   *  Server-side bucket strings: trade / buy / sell / redeem / loss /
   *  refund / cancel / other. */
  bucket?: string;
  /** Scope the feed to one or more markets (an event's member markets),
   *  sent as repeated ?market_id= params. The gateway filters the merged
   *  activity stream to these markets server-side. Omitted/empty → the
   *  user's whole portfolio (the standalone History tab). */
  marketIds?: string[];
}

export const fetchPortfolioActivity = (
  opts: PortfolioActivityOpts,
): Promise<Paginated<PortfolioActivityItem>> =>
  api<{ items: PortfolioActivityItem[]; page: PageInfo }>(
    `/api/portfolio/activity${qs({
      limit: opts.limit,
      offset: opts.offset,
      ...sortQS(opts.sort),
      bucket: opts.bucket && opts.bucket !== "all" ? opts.bucket : undefined,
      market_id: opts.marketIds && opts.marketIds.length ? opts.marketIds : undefined,
      // SM-482/SM-483: the gateway threads this into EVERY leg of the
      // composed feed — orders, position events, fees, disputes and
      // the ledger wallet leg, whose CurrenciesForMode filter is what
      // keeps GOLD deposits and GOLD promos out of a GAME history.
      // Absent in CASH so the legacy request stays byte-identical.
      ...modeQS(),
    })}`,
  ).then((r) => ({ items: r.items ?? [], page: r.page }));

// fetchPortfolioFees retrieves the authenticated user's fee_charges,
// optionally scoped to a single market. The History tab fetches the
// recent slice without a market filter and joins client-side on
// trade_id. The Redeem modal passes ?market_id to scope the rollup
// to one position's lifetime fees.
export interface PortfolioFeesOpts extends PageOpts {
  market_id?: string;
}
export const fetchPortfolioFees = (
  opts: PortfolioFeesOpts,
): Promise<Paginated<FeeChargeItem>> =>
  api<{ fees: FeeChargeItem[]; page: PageInfo }>(
    `/api/portfolio/fees${qs({
      limit: opts.limit,
      offset: opts.offset,
      market_id: opts.market_id,
    })}`,
  ).then((r) => ({ items: r.fees ?? [], page: r.page }));

// fetchMyFees retrieves the authenticated user's ACTIVE special-fee
// overrides (SM-139/SM-144). Empty list = the user trades on the
// market default rates that already ride on every market payload. The
// trade panel merges: market-scoped override ?? user-global override
// (market_id absent) ?? market defaults. Rates are int64 minor-unit
// strings scaled by fee_rate_precision — same encoding as the market
// payload's maker/taker_fee_rate.
export interface MyFeeOverride {
  override_id: string;
  market_id?: string;
  maker_fee_rate: string;
  taker_fee_rate: string;
  fee_rate_precision: number;
}
export const fetchMyFees = (): Promise<MyFeeOverride[]> =>
  api<{ overrides: MyFeeOverride[] }>("/api/me/fees").then(
    (r) => r.overrides ?? [],
  );

// Redeem — the money-moving API. Requires an Idempotency-Key header;
// the gateway re-injects it into the body for settlement-service, but
// the header is the dedup key the gateway logs and the audit trail
// references. crypto.randomUUID() gives us a ULID-ish stable value per
// click; the consumer should cache it across retries.
export const redeemPosition = (
  positionID: string,
  idempotencyKey: string,
  expectedFeeVersion?: number,
  expectedBrokerFeeVersion?: string,
): Promise<RedeemPositionResponse> => {
  // The fee versions the user was quoted. If the platform has changed the
  // exchange rate (SM-327, SETTLEMENT_000702) or the broker's plan version
  // (SM-741, STALE_BROKER_FEE_VERSION) since, the server rejects rather
  // than charging a fee the user never saw. Each rides only when the quote
  // carried it, so a legacy quote still sends the legacy body.
  const body: { expected_fee_version?: number; expected_broker_fee_version?: string } = {};
  if (expectedFeeVersion) body.expected_fee_version = expectedFeeVersion;
  if (expectedBrokerFeeVersion) body.expected_broker_fee_version = expectedBrokerFeeVersion;
  return api<RedeemPositionResponse>(`/api/positions/${positionID}/redeem`, {
    method: "POST",
    headers: { "Idempotency-Key": idempotencyKey },
    body: Object.keys(body).length ? body : undefined,
  });
};

// fetchRedeemQuote — the pre-confirmation preview: gross payout,
// redemption fee, and the net the user will actually receive.
//
// A read, so no idempotency key. Deliberately server-side: the fee
// rounds twice before it lands on these numbers, and an estimate
// computed here would drift from what is charged.
export const fetchRedeemQuote = (positionID: string): Promise<RedeemQuote> =>
  api<RedeemQuote>(`/api/positions/${positionID}/redeem-quote`);

// Close — the loser-side dismiss API. Counterpart to redeem for LOST
// positions (rule resolved, payout=0). No money moves; no idempotency
// key required because the operation is non-monetary and a duplicate
// is a safe no-op. Backend transitions LOST → CLOSED and the position
// auto-archives to History.
export const closePosition = (
  positionID: string,
): Promise<ClosePositionResponse> =>
  api<ClosePositionResponse>(`/api/positions/${positionID}/close`, {
    method: "POST",
  });

// MFA register input — mirrors contracts/httpapi/v1.RegisterFactorRequest.
// FactorKind is the canonical wire string (TOTP / EMAIL_OTP / PASSPHRASE /
// WEBAUTHN / PASSKEY / BACKUP_CODE); per-kind fields are optional and
// only one applies at a time.
export interface RegisterMFAInput {
  kind: "TOTP" | "EMAIL_OTP" | "PASSPHRASE" | "WEBAUTHN" | "PASSKEY" | "BACKUP_CODE";
  label?: string;
  passphrase?: string;
  email?: string;
  totp_code?: string;
}

export const registerMFAFactor = (input: RegisterMFAInput) =>
  api<{ factor: MFAFactor; user_visible?: Record<string, unknown> }>(
    "/api/me/mfa/factors",
    {
      method: "POST",
      body: {
        factor_kind: input.kind,
        friendly_name: input.label ?? "",
        email: input.email,
        passphrase: input.passphrase,
      },
    },
  );

// verifyMFAFactor activates a PENDING_VERIFICATION factor by submitting
// the first valid 6-digit code from the user's authenticator app or
// the OTP they received via email. The server flips the factor to
// ACTIVE; returns the activated factor row so the UI can update.
export const verifyMFAFactor = (factorId: string, code: string) =>
  api<{ factor: MFAFactor }>(
    `/api/me/mfa/factors/${factorId}/verify`,
    { method: "POST", body: { code } },
  );

export const revokeMFAFactor = (factorId: string) =>
  api<{ ok: boolean }>(`/api/me/mfa/factors/${factorId}`, { method: "DELETE" });

export const revokeSession = (sessionId: string) =>
  api<{ ok: boolean }>(`/api/me/sessions/${sessionId}`, { method: "DELETE" });

// Mirrors contracts/httpapi/v1.ChangePasswordRequest. user_id is
// injected by the gateway from the session; we only send the two
// password fields.
export interface ChangePasswordInput {
  current_password: string;
  new_password: string;
}

export const changePassword = (input: ChangePasswordInput) =>
  api<{ ok: boolean }>("/api/me/password", { method: "POST", body: input });

export interface UpdateProfileInput {
  display_name?: string;
  // BCP-47-ish UI language (contracts UpdateProfileRequest.Locale). The
  // LanguageProvider PATCHes it on switch so the choice follows the account.
  locale?: string;
}

export const updateProfile = (input: UpdateProfileInput) =>
  api<ProfileResponse>("/api/me/profile", { method: "PATCH", body: input });

// Avatar upload / removal. uploadAvatar posts a multipart body (field
// "file") to the gateway, which streams it to client-service; both
// return the refreshed profile so the caller can re-render the new
// avatar without a separate refetch. removeAvatar clears it.
export const uploadAvatar = (file: File) => {
  const fd = new FormData();
  fd.append("file", file);
  return api<ProfileResponse>("/api/me/avatar", { method: "POST", body: fd });
};

export const removeAvatar = () =>
  api<ProfileResponse>("/api/me/avatar", { method: "DELETE" });

// --- Preferences (server-authoritative per-user config; Phase 1+) ---
// Well-known keys — mirror contracts/events/v1 + the client-service
// registry. Never reassign these strings.
export const PREF_AUTO_REDEEM_ENABLED = "trading.auto_redeem.enabled";
export const PREF_AUTO_REDEEM_PROMPT_SEEN = "trading.auto_redeem.prompt_seen";
export const PREF_ONE_CLICK_SELL_ENABLED = "trading.one_click_sell.enabled";

export const fetchPreferences = () =>
  api<PreferencesResponse>("/api/me/preferences");

// updatePreferences PATCHes one or more keys; user_id is gateway-injected.
// Each value must match its registry-declared type (booleans for the
// auto_redeem.* keys). The response echoes the full effective set.
export const updatePreferences = (
  patch: Record<string, boolean | string | number>,
) =>
  api<PreferencesResponse>("/api/me/preferences", {
    method: "PATCH",
    body: { preferences: patch },
  });

// --- watchlist ---
// Server-side bookmarks. fetchWatchlist returns just the market-group handles
// (newest save first); the Watchlist page resolves each to a MarketGroup via
// fetchMarketGroupByHandle. user_id is gateway-injected. We request the
// server's max page (200) — watchlists are small and each handle resolves with
// its own query, so a single page is plenty; real paging can come later.
export const fetchWatchlist = (): Promise<string[]> =>
  api<WatchlistResponse>(`/api/me/watchlist${qs({ limit: 200, offset: 0 })}`).then(
    (r) => (r.watchlist ?? []).map((e) => e.market_group_id),
  );

// add/remove are idempotent on the server (PK conflict = no-op / delete-if-
// present), so the optimistic toggle in useWatchlist can fire them freely.
export const addToWatchlist = (marketGroupId: string) =>
  api("/api/me/watchlist", { method: "POST", body: { market_group_id: marketGroupId } });

export const removeFromWatchlist = (marketGroupId: string) =>
  api(`/api/me/watchlist/${encodeURIComponent(marketGroupId)}`, { method: "DELETE" });

// --- comments ---
// Per-market-group discussion threads (client-service via the gateway).
// Reads are public — my_vote personalizes to the session when one
// exists. market_group_id is the same handle the watchlist stores
// (group.id: event_id when grouped, market_id when loose).

export type CommentSort = "top" | "new";

export interface CommentItem {
  comment_id: string;
  market_group_id: string;
  // parent_id/root_id are absent on top-level comments. parent_id is
  // the row replied to (the tree edge the UI nests under); root_id the
  // top-level ancestor whose replies feed carries the whole thread.
  parent_id?: string;
  root_id?: string;
  user_id: string;
  author_name: string;
  author_avatar_url?: string;
  body: string;
  // DELETED rows arrive as blanked placeholders while they still
  // anchor visible descendants (top-level rows and mid-thread parents
  // alike); fully-dead chains never arrive at all.
  status: "ACTIVE" | "DELETED" | "HIDDEN";
  upvotes: number;
  downvotes: number;
  my_vote: -1 | 0 | 1;
  reply_count: number;
  created_at: string;
}

export interface CommentSummary {
  total: number;
  samples: CommentItem[];
}

export interface CommentVoteResult {
  comment_id: string;
  upvotes: number;
  downvotes: number;
  my_vote: -1 | 0 | 1;
}

export const fetchComments = (
  groupId: string,
  sort: CommentSort,
  limit: number,
  offset: number,
): Promise<Paginated<CommentItem>> =>
  api<{ comments: CommentItem[]; page: PageInfo }>(
    `/api/comments${qs({ market_group_id: groupId, sort, limit, offset })}`,
  ).then((r) => ({ items: r.comments ?? [], page: r.page }));

// Replies always load chronologically (server-fixed, no sort param):
// a parent is strictly older than its children, so every loaded
// prefix is a complete forest buildReplyTree can hang nodes from.
export const fetchCommentReplies = (
  rootId: string,
  limit: number,
  offset: number,
): Promise<Paginated<CommentItem>> =>
  api<{ comments: CommentItem[]; page: PageInfo }>(
    `/api/comments/${encodeURIComponent(rootId)}/replies${qs({ limit, offset })}`,
  ).then((r) => ({ items: r.comments ?? [], page: r.page }));

export const fetchCommentSummary = (groupId: string, samples = 5): Promise<CommentSummary> =>
  api<CommentSummary>(`/api/comments/summary${qs({ market_group_id: groupId, samples })}`);

export interface PostCommentInput {
  market_group_id: string;
  parent_id?: string;
  body: string;
}

export const postComment = (input: PostCommentInput): Promise<CommentItem> =>
  api<{ comment: CommentItem }>("/api/comments", { method: "POST", body: input }).then(
    (r) => r.comment,
  );

// value is the TARGET state (1 / -1 / 0), not a delta — the caller
// computes "tap the active arrow clears it", so retries are idempotent.
export const voteComment = (commentId: string, value: -1 | 0 | 1): Promise<CommentVoteResult> =>
  api<CommentVoteResult>(`/api/comments/${encodeURIComponent(commentId)}/vote`, {
    method: "POST",
    body: { value },
  });

export const deleteComment = (commentId: string) =>
  api(`/api/comments/${encodeURIComponent(commentId)}`, { method: "DELETE" });

// --- notifications (server-backed bell; notification-service) ---

export interface NotificationUnreadCount {
  unread_count: number;
}

// One feed page. The envelope also carries unread_count, but the badge
// reads the dedicated keys.notificationsUnread query so it updates
// without the feed mounted.
export const fetchNotifications = (opts: {
  limit: number;
  offset: number;
}): Promise<Paginated<NotificationItem>> =>
  api<{ items: NotificationItem[]; page: PageInfo }>(
    `/api/notifications${qs({ limit: opts.limit, offset: opts.offset })}`,
  ).then((r) => ({ items: r.items ?? [], page: r.page }));

export const fetchNotificationsUnreadCount = (): Promise<NotificationUnreadCount> =>
  api<NotificationUnreadCount>("/api/notifications/unread-count");

export const markNotificationRead = (id: string): Promise<NotificationUnreadCount> =>
  api<NotificationUnreadCount>(
    `/api/notifications/${encodeURIComponent(id)}/read`,
    { method: "POST" },
  );

export const markAllNotificationsRead = (): Promise<NotificationUnreadCount> =>
  api<NotificationUnreadCount>("/api/notifications/read-all", { method: "POST" });
